"use strict";
/*******************************************************************************

    The class that defines the transaction's inputs of a block.

    Copyright:
        Copyright (c) 2020-2021 BOSAGORA Foundation
        All rights reserved.

    License:
        MIT License. See LICENSE for details.

*******************************************************************************/
Object.defineProperty(exports, "__esModule", { value: true });
exports.TxInput = void 0;
var Hash_1 = require("../common/Hash");
var Signature_1 = require("../common/Signature");
var Lock_1 = require("../script/Lock");
var JSONValidator_1 = require("../utils/JSONValidator");
var Utils_1 = require("../utils/Utils");
var VarInt_1 = require("../utils/VarInt");
/**
 * The class that defines the transaction's inputs of a block.
 * Convert JSON object to TypeScript's instance.
 * An exception occurs if the required property is not present.
 */
var TxInput = /** @class */ (function () {
    /**
     * Constructor
     * @param hash  The hash of the UTXO or the hash of the transaction
     * @param unlock The instance of Unlock
     * @param unlock_age The UTXO this `Input` references must be at least
     * `unlock_age` older than the block height at which the spending
     * transaction wants to be  included in the block.
     * Use for implementing relative time locks.
     */
    function TxInput(hash, unlock, unlock_age) {
        if (unlock === void 0) { unlock = Lock_1.Unlock.Null; }
        if (unlock_age === void 0) { unlock_age = 0; }
        this.utxo = hash;
        this.unlock = unlock;
        this.unlock_age = unlock_age;
    }
    TxInput.fromTxHash = function (hash, index, unlock, unlock_age) {
        if (unlock === void 0) { unlock = Lock_1.Unlock.Null; }
        if (unlock_age === void 0) { unlock_age = 0; }
        return new TxInput(Hash_1.makeUTXOKey(hash, index), unlock, unlock_age);
    };
    /**
     * The reviver parameter to give to `JSON.parse`
     *
     * This function allows to perform any necessary conversion,
     * as well as validation of the final object.
     *
     * @param key   Name of the field being parsed
     * @param value The value associated with `key`
     * @returns A new instance of `TxInputs` if `key == ""`, `value` otherwise.
     */
    TxInput.reviver = function (key, value) {
        if (key !== "")
            return value;
        JSONValidator_1.JSONValidator.isValidOtherwiseThrow("TxInput", value);
        return new TxInput(new Hash_1.Hash(value.utxo), Lock_1.Unlock.reviver("", value.unlock), value.unlock_age);
    };
    /**
     * Collects data to create a hash.
     * @param buffer The buffer where collected data is stored
     */
    TxInput.prototype.computeHash = function (buffer) {
        this.utxo.computeHash(buffer);
        Hash_1.hashPart(this.unlock_age, buffer);
    };
    /**
     * Returns the data size.
     */
    TxInput.prototype.getNumberOfBytes = function () {
        return (Hash_1.Hash.Width + // TxInput.utxo
            this.unlock.getNumberOfBytes() + // TxInput.unlock
            Utils_1.Utils.SIZE_OF_INT); // TxInput.unlock_age
    };
    /**
     * Returns the estimated data size.
     */
    TxInput.getEstimatedNumberOfBytes = function () {
        return Hash_1.Hash.Width + Utils_1.Utils.SIZE_OF_INT + Signature_1.Signature.Width + Utils_1.Utils.SIZE_OF_BYTE;
    };
    /**
     * The compare function of TxInput
     */
    TxInput.compare = function (a, b) {
        return Utils_1.Utils.compareBuffer(a.utxo.data, b.utxo.data);
    };
    /**
     * Serialize as binary data.
     * @param buffer - The buffer where serialized data is stored
     */
    TxInput.prototype.serialize = function (buffer) {
        this.utxo.serialize(buffer);
        this.unlock.serialize(buffer);
        VarInt_1.VarInt.fromNumber(this.unlock_age, buffer);
    };
    /**
     * Deserialize as binary data.
     * @param buffer - The buffer to be deserialized
     */
    TxInput.deserialize = function (buffer) {
        var utxo = Hash_1.Hash.deserialize(buffer);
        var unlock = Lock_1.Unlock.deserialize(buffer);
        var unlock_age = VarInt_1.VarInt.toNumber(buffer);
        return new TxInput(utxo, unlock, unlock_age);
    };
    /**
     * Creates and returns a copy of this object.
     */
    TxInput.prototype.clone = function () {
        return new TxInput(this.utxo.clone(), this.unlock.clone(), this.unlock_age);
    };
    return TxInput;
}());
exports.TxInput = TxInput;
//# sourceMappingURL=TxInput.js.map